import { useMemo, useRef } from 'react';
import { Animated, Image, ImageSourcePropType, PanResponder, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import colors from '../theme';
import { usePlayback } from '../context/PlaybackContext';

type MiniPlayerProps = {
  onOpen?: () => void;
};

const coverFallback = require('../../assets/aural-continue-thumb.png');

function imageSource(url: string | null | undefined): ImageSourcePropType {
  return url ? { uri: url } : coverFallback;
}

export default function MiniPlayer({ onOpen }: MiniPlayerProps) {
  const playback = usePlayback();
  const dragX = useRef(new Animated.Value(0)).current;

  const resetDrag = () => {
    Animated.spring(dragX, {
      toValue: 0,
      useNativeDriver: true,
      tension: 90,
      friction: 12,
    }).start();
  };

  const dismiss = (direction: number) => {
    Animated.timing(dragX, {
      toValue: direction * 420,
      duration: 180,
      useNativeDriver: true,
    }).start(() => {
      dragX.setValue(0);
      playback.stop();
    });
  };

  const panResponder = useMemo(
    () => PanResponder.create({
      onMoveShouldSetPanResponder: (_, gesture) => Math.abs(gesture.dx) > 12 && Math.abs(gesture.dx) > Math.abs(gesture.dy),
      onPanResponderGrant: () => {
        dragX.stopAnimation();
      },
      onPanResponderMove: (_, gesture) => {
        dragX.setValue(gesture.dx);
      },
      onPanResponderRelease: (_, gesture) => {
        if (Math.abs(gesture.dx) > 90) {
          dismiss(gesture.dx > 0 ? 1 : -1);
        } else {
          resetDrag();
        }
      },
      onPanResponderTerminate: resetDrag,
    }),
    [dragX],
  );
  const animatedStyle = {
    opacity: dragX.interpolate({
      inputRange: [-180, 0, 180],
      outputRange: [0.15, 1, 0.15],
      extrapolate: 'clamp' as const,
    }),
    transform: [
      { translateX: dragX },
      {
        scale: dragX.interpolate({
          inputRange: [-180, 0, 180],
          outputRange: [0.96, 1, 0.96],
          extrapolate: 'clamp' as const,
        }),
      },
    ],
  };

  if (!playback.book || !playback.minimized) {
    return null;
  }

  return (
    <View pointerEvents="box-none" style={styles.host}>
      <Animated.View style={animatedStyle} {...panResponder.panHandlers}>
        <Pressable style={styles.container} onPress={onOpen}>
          <View style={styles.thumbnailWrap}>
            <Image source={imageSource(playback.cover)} style={styles.thumbnailGlow} resizeMode="cover" />
            <Image source={imageSource(playback.cover)} style={styles.thumbnail} resizeMode="cover" />
          </View>
          <View style={styles.textBlock}>
            <Text style={styles.title} numberOfLines={1}>{playback.title}</Text>
            <Text style={styles.author} numberOfLines={1}>{playback.author}</Text>
            {playback.chapterTitle ? <Text style={styles.chapter} numberOfLines={1}>{playback.chapterTitle}</Text> : null}
            <View style={styles.progressTrack}>
              <View style={[styles.progressFill, { width: `${Math.max(playback.progress * 100, 4)}%` }]} />
            </View>
          </View>
          <Pressable
            style={styles.playButton}
            onPress={(event) => {
              event.stopPropagation();
              playback.togglePlay();
            }}
            hitSlop={8}
          >
            <Ionicons name={playback.isPlaying ? 'pause' : 'play'} size={24} color={colors.textPrimary} />
          </Pressable>
        </Pressable>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  host: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 90,
    zIndex: 50,
  },
  container: {
    height: 94,
    backgroundColor: '#0B0B0C',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingLeft: 20,
    paddingRight: 30,
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
  },
  thumbnailWrap: {
    width: 56,
    height: 56,
    marginRight: 16,
  },
  thumbnailGlow: {
    position: 'absolute',
    left: 5,
    top: 9,
    width: 46,
    height: 46,
    borderRadius: 8,
    opacity: 0.45,
  },
  thumbnail: {
    width: 56,
    height: 56,
    borderRadius: 8,
    backgroundColor: colors.ink,
  },
  textBlock: {
    flex: 1,
    paddingRight: 16,
  },
  title: {
    color: colors.buttonText,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  author: {
    color: '#A9AAB3',
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 6,
  },
  chapter: {
    color: colors.button,
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 8,
  },
  progressTrack: {
    height: 3,
    borderRadius: 10,
    backgroundColor: '#24242A',
    overflow: 'hidden',
  },
  progressFill: {
    height: 3,
    borderRadius: 10,
    backgroundColor: colors.button,
  },
  playButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.buttonText,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
